<div class="order-detail-dashboard">
  <!-- Dashboard Header -->
  <div class="dashboard-header">
    <div class="header-left">
      <button class="btn btn-icon" (click)="goBack()" title="Back to Orders">
        <i class="fas fa-arrow-left"></i>
      </button>
      <div class="header-title">
        <h1>Order Dashboard</h1>
        <p class="order-subtitle">{{ order?.id || 'Loading...' }}</p>
      </div>
    </div>
    <div class="header-actions">
      <button class="btn btn-outline-secondary btn-compact" (click)="loadOrder()" [disabled]="loading"
        title="Refresh Order Data">
        <i class="fas fa-sync-alt" [class.fa-spin]="loading"></i>
        <span>Refresh</span>
      </button>
    </div>
  </div>

  <!-- Loading State -->
  @if (loading && !order) {
  <div class="loading-state">
    <div class="spinner"></div>
    <p>Loading order details...</p>
  </div>
  }

  <!-- Error State -->
  @if (error && !loading && !order) {
  <div class="error-state">
    <div class="error-content">
      <i class="fas fa-exclamation-triangle fa-2x"></i>
      <h3>Failed to load order</h3>
      <p>{{ error }}</p>
      <button class="btn btn-primary" (click)="loadOrder()">
        <i class="fas fa-redo"></i>
        Try Again
      </button>
    </div>
  </div>
  }

  <!-- Dashboard Content -->
  @if (!loading && !error && order) {
  <!-- Key Metrics Cards -->
  <div class="metrics-grid">
    <div class="metric-card status-card" [ngClass]="getStatusClass(order.status)">
      <div class="metric-icon">
        <i class="fas fa-info-circle"></i>
      </div>
      <div class="metric-content">
        <label>Order Status</label>
        @if (order.status === 'delivered') {
        <!-- Read-only display for delivered orders -->
        <div class="status-display-readonly">
          <span class="status-badge status-delivered">
            Delivered
            @if (order.deliveredDate) {
            <span class="status-date">{{ formatDateTime(order.deliveredDate) }}</span>
            }
          </span>
          <small class="delivered-note">Marked as delivered by Shiprocket</small>
        </div>
        } @else if (order.status === 'shipped') {
        <!-- Read-only display for shipped orders -->
        <div class="status-display-readonly">
          <span class="status-badge status-shipped">
            Shipped
            @if (order.shippingDate) {
            <span class="status-date">{{ formatDateTime(order.shippingDate) }}</span>
            }
          </span>
          <small class="shipped-note">
            <i class="fas fa-lock"></i> Status cannot be changed once shipped
          </small>
        </div>
        } @else {
        <!-- Editable dropdown - filtered options based on current status -->
        <select [ngModel]="order.status" (ngModelChange)="onStatusChange($event)" class="status-select"
          [ngClass]="getStatusClass(order.status)">
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="shipped">Shipped</option>
          <option value="cancelled">Cancelled</option>
        </select>
        }
      </div>
    </div>

    <div class="metric-card products-card">
      @if (order.products && order.products.length > 0) {
      <div class="product-image-main">
        <img [src]="getProductImage(order.products[0])" [alt]="getLocalizedName(order.products[0])"
          class="main-product-img"
          (error)="$any($event.target).src='https://placehold.co/80x80/9E9E9E/white?text=Product'"
          onerror="this.onerror=null; this.src='https://placehold.co/80x80/9E9E9E/white?text=Product'">
      </div>
      }
      <div class="metric-content">
        <label>Products & Amount</label>
        <div class="amount-display">
          @if (order.products && order.products.length > 0) {
          <div class="product-name-display">
            {{ getDisplayNameWithCategory(order.products[0]) }}
          </div>
          }
          <h3>₹{{ getOrderTotal(order) }}</h3>
          @if (order.discountAmount && order.discountAmount > 0) {
          <small class="discount-text">-₹{{ order.discountAmount }} discount</small>
          }
        </div>
      </div>
    </div>

    <div class="metric-card sub-products-card">
      <div class="metric-icon">
        <i class="fas fa-box"></i>
      </div>
      <div class="metric-content">
        <label>Items in Box</label>
        <h3>{{ getSubProducts() }}</h3>
      </div>
    </div>
  </div>


  <!-- Main Dashboard Grid -->
  <div class="dashboard-grid">
    <!-- Customer Information Card -->
    <div class="dashboard-card customer-card">
      <div class="card-header">
        <div class="header-left">
          <i class="fas fa-user"></i>
          <h3>Customer Information</h3>
        </div>
        @if (hasCustomerPhoto()) {
        <button class="btn btn-icon-small" (click)="openImageModal(getCustomerPhotoUrl(), customerImageModal)"
          title="View Photo">
          <i class="fas fa-image"></i>
        </button>
        }
      </div>
      <div class="card-body">
        <div class="info-grid">
          <div class="info-item">
            <label><i class="fas fa-user"></i> Name</label>
            <span>{{ order.userName }}</span>
          </div>
          <div class="info-item">
            <label><i class="fas fa-phone"></i> Mobile</label>
            <span>{{ order.userMobile }}</span>
          </div>
          <div class="info-item">
            <label><i class="fas fa-envelope"></i> Email</label>
            <span>{{ order.userEmail || 'Not provided' }}</span>
          </div>
          @if (order.userAltMobile) {
          <div class="info-item">
            <label><i class="fas fa-phone-alt"></i> Alternate Mobile</label>
            <span>{{ order.userAltMobile }}</span>
          </div>
          }
          <div class="info-item">
            <label><i class="fas fa-map-marker-alt"></i> Address</label>
            <span class="address-text">{{ order.userFullAddress || order.deliveryAddress || 'Not provided' }}</span>
          </div>
          <div class="info-item">
            <label><i class="fas fa-venus-mars"></i> Gender</label>
            <span>{{ getGenderText(order.userGender || '') }}</span>
          </div>
          @if (order.userCurrentWeight) {
          <div class="info-item">
            <label><i class="fas fa-weight"></i> Weight</label>
            <span>{{ formatWeight(order.userCurrentWeight) }}</span>
          </div>
          }
          @if (order.userCurrentHeight) {
          <div class="info-item">
            <label><i class="fas fa-ruler-vertical"></i> Height</label>
            <span>{{ formatHeight(order.userCurrentHeight) }}</span>
          </div>
          }
          @if (order.userTakenDose && order.userTakenDose > 0) {
          <div class="info-item">
            <label><i class="fas fa-pills"></i> Doses Taken</label>
            <span>{{ order.userTakenDose }} / 3</span>
          </div>
          }
        </div>
      </div>
    </div>

    <!-- Order Details Card -->
    <div class="dashboard-card order-card">
      <div class="card-header">
        <div class="header-left">
          <i class="fas fa-shopping-bag"></i>
          <h3>Order Details</h3>
        </div>
      </div>
      <div class="card-body">
        <div class="info-grid">
          <div class="info-item">
            <label><i class="fas fa-hashtag"></i> Order ID</label>
            <span class="order-id-text">{{ order.id }}</span>
          </div>
          <div class="info-item">
            <label><i class="fas fa-calendar-check"></i> Order Date</label>
            <span>{{ formatDate(order.orderDate) }}</span>
          </div>
          @if (order.confirmedDate) {
          <div class="info-item">
            <label><i class="fas fa-check-circle"></i> Confirmed Date</label>
            <span>{{ formatDate(order.confirmedDate) }}</span>
          </div>
          }
          @if (order.shippingDate) {
          <div class="info-item">
            <label><i class="fas fa-shipping-fast"></i> Shipped Date</label>
            <span>{{ formatDate(order.shippingDate) }}</span>
          </div>
          }
          @if (order.deliveredDate) {
          <div class="info-item">
            <label><i class="fas fa-box-check"></i> Delivered Date</label>
            <span>{{ formatDate(order.deliveredDate) }}</span>
          </div>
          }
          <div class="info-item">
            <label><i class="fas fa-shopping-cart"></i> Products Count</label>
            <span>{{ order.products.length }} items</span>
          </div>
          <div class="info-item">
            <label><i class="fas fa-tags"></i> Order Type</label>
            <span class="order-type-badge" [ngClass]="order.isComboOrder ? 'combo' : 'single'">
              {{ order.isComboOrder ? 'Combo Order' : 'Single Order' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Amount Breakdown Section -->
      <div class="card-body amount-breakdown">
        <h4 class="section-title">
          <i class="fas fa-calculator"></i> Amount Breakdown
        </h4>
        <div class="amount-grid">
          <div class="amount-item">
            <label><i class="fas fa-box"></i> Product Amount</label>
            <span class="amount-value">₹{{ getProductAmount(order) }}</span>
          </div>
          <div class="amount-item">
            <label><i class="fas fa-truck"></i> Delivery Charges</label>
            <span class="amount-value">₹{{ getDeliveryCharges(order) }}</span>
          </div>
          <div class="amount-item grand-total-item">
            <label><i class="fas fa-receipt"></i> Grand Total</label>
            <span class="amount-value grand-total">₹{{ getGrandTotal(order) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Shiprocket Shipping & Integration (Unified Section) -->
    <div class="dashboard-card shiprocket-shipping">
      <div class="card-header shiprocket-header">
        <div class="header-left">
          <span class="icon">🚀</span>
          <h3>Shiprocket Shipping & Integration</h3>
          @if (order.shiprocketShipmentId) {
          <span class="status-badge active">
            <span class="icon">✅</span> Active Shipment
          </span>
          } @else {
          <span class="status-badge inactive">
            <span class="icon">⚠️</span> Not Created
          </span>
          }
        </div>
      </div>
      <div class="card-body">
        @if (order.shiprocketShipmentId) {
        <!-- Shiprocket Details (When Shipment Exists) -->
        <div class="shiprocket-unified-content">
          <!-- Shipping Information Section -->
          <div class="section-group">
            <h4 class="section-title">
              <span class="icon">🚚</span> Shipping Information
            </h4>
            <div class="info-grid">
              <div class="info-item">
                <label>
                  <span class="icon">📦</span> Tracking Number (AWB Code)
                </label>
                <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
                  @if (order.shiprocketAwbCode) {
                    <span class="awb-code-display">{{ order.shiprocketAwbCode }}</span>
                  } @else {
                    <span class="pending-text">
                      <span class="icon">⏳</span> Pending (AWB will be assigned after courier validation)
                    </span>
                  }
                  @if (order.shiprocketShipmentId && !order.shiprocketAwbCode) {
                    <button type="button" class="btn btn-sm btn-outline-primary" (click)="refreshShipmentStatus()" [disabled]="loading" title="Refresh AWB code from Shiprocket">
                      <span class="icon">🔄</span> Refresh Status
                    </button>
                  }
                </div>
              </div>

              @if (order.shiprocketCourierName) {
                <div class="info-item">
                  <label>
                    <span class="icon">🚚</span> Courier Partner
                  </label>
                  <span class="courier-name">{{ order.shiprocketCourierName }}</span>
                </div>
              }

              @if (shiprocketTrackingData?.status) {
                <div class="info-item">
                  <label>
                    <span class="icon">ℹ️</span> Shipment Status
                  </label>
                  <span class="shipment-status">{{ shiprocketTrackingData.status }}</span>
                </div>
              }

              @if (shiprocketTrackingData?.etd) {
                <div class="info-item">
                  <label>
                    <span class="icon">📅</span> Estimated Delivery
                  </label>
                  <span>{{ shiprocketTrackingData.etd }}</span>
                </div>
              } @else if (estimatedDeliveryDate) {
                <div class="info-item">
                  <label>
                    <span class="icon">📅</span> Estimated Delivery
                  </label>
                  <span>{{ formatDateFromString(estimatedDeliveryDate) }}</span>
                </div>
              }

              @if (order.shippingDate) {
                <div class="info-item">
                  <label>
                    <span class="icon">🚚</span> Shipped Date
                  </label>
                  <span>{{ formatDate(order.shippingDate) }}</span>
                </div>
              }

              @if (order.deliveredDate) {
                <div class="info-item">
                  <label>
                    <span class="icon">📦</span> Delivered Date
                  </label>
                  <span>{{ formatDate(order.deliveredDate) }}</span>
                </div>
              }
            </div>
          </div>

          <!-- Integration Details Section -->
          <div class="section-group">
            <h4 class="section-title">
              <span class="icon">🔌</span> Integration Details
            </h4>
            <div class="info-grid">
              <div class="info-item">
                <label>Shiprocket Order ID</label>
                <span>{{ order.shiprocketOrderId || 'N/A' }}</span>
              </div>
              <div class="info-item">
                <label>Shipment ID</label>
                <span>{{ order.shiprocketShipmentId }}</span>
              </div>
            </div>
          </div>

          <!-- Actions Section -->
          <div class="section-group">
            <h4 class="section-title">
              <span class="icon">🛠️</span> Actions
            </h4>
            <div class="actions-grid">
              @if (!shiprocketTrackingData) {
              <button type="button" class="action-btn btn-track" (click)="trackShipment()" [disabled]="loading"
                title="Fetch real-time tracking from Shiprocket">
                <span class="icon">🔄</span>
                <span>Fetch Tracking</span>
              </button>
              }
              <button type="button" class="action-btn btn-label" (click)="generateLabel()" [disabled]="loading">
                <span class="icon">🏷️</span>
                <span>Generate Label</span>
              </button>
              <button type="button" class="action-btn btn-invoice" (click)="generateInvoice()"
                [disabled]="exporting || !order">
                <span class="icon">📄</span>
                <span>{{ exporting ? 'Exporting...' : 'Export Invoice' }}</span>
              </button>
              <button type="button" class="action-btn btn-manifest" (click)="generateManifest()" [disabled]="loading">
                <span class="icon">📋</span>
                <span>Generate Manifest</span>
              </button>
              <button type="button" class="action-btn btn-pickup" (click)="generatePickup()" [disabled]="loading">
                <span class="icon">🚚</span>
                <span>Request Pickup</span>
              </button>
            </div>
          </div>

          <!-- Generated Documents Section -->
          @if (order.shiprocketManifestUrl || order.shiprocketLabelUrl || order.shiprocketInvoiceUrl) {
          <div class="section-group">
            <h4 class="section-title">
              <span class="icon">📑</span> Generated Documents
            </h4>
            <div class="document-links">
              @if (order.shiprocketManifestUrl) {
              <a [href]="order.shiprocketManifestUrl" target="_blank" class="doc-link">
                <span class="icon">📋</span> Manifest
              </a>
              }
              @if (order.shiprocketLabelUrl) {
              <a [href]="order.shiprocketLabelUrl" target="_blank" class="doc-link">
                <span class="icon">🏷️</span> Label
              </a>
              }
              @if (order.shiprocketInvoiceUrl) {
              <a [href]="order.shiprocketInvoiceUrl" target="_blank" class="doc-link">
                <span class="icon">📄</span> Invoice
              </a>
              }
            </div>
          </div>
          }
        </div>
        } @else {
        <!-- Waiting for Shiprocket Shipment -->
        <div class="waiting-shiprocket">
          <div class="waiting-content">
            <span class="icon large-icon">🚀</span>
            <h4>Shiprocket Shipment Not Created</h4>
            <p>Create a Shiprocket shipment to get tracking information, generate shipping labels, and access all
              shipping features.</p>
            <button class="btn btn-primary" (click)="createShiprocketShipment()"
              [disabled]="loading || order.status !== 'shipped'">
              <span class="icon">➕</span>
              Create Shiprocket Shipment
            </button>
            @if (order.status !== 'shipped') {
            <small class="text-muted">
              <span class="icon">ℹ️</span> Change order status to "Shipped" first, then create shipment
            </small>
            }
          </div>
        </div>
        }
      </div>
    </div>



    <!-- Order History Card -->
    @if (getOrderHistory().length > 0) {
    <div class="dashboard-card history-card">
      <div class="card-header">
        <div class="header-left">
          <i class="fas fa-history"></i>
          <h3>Order History</h3>
        </div>
      </div>
      <div class="card-body">
        <div class="timeline">
          @for (event of getOrderHistory(); track event.date; let i = $index) {
          <div class="timeline-item">
            <div class="timeline-marker"></div>
            <div class="timeline-content">
              <div class="timeline-header">
                <h4>{{ event.status }}</h4>
                <span class="timeline-date">{{ formatDate(event.date) }}</span>
              </div>
              <p>{{ event.description }}</p>
            </div>
          </div>
          }
        </div>
      </div>
    </div>
    }

  </div>
  }

  <!-- Customer Image Modal -->
  <ng-template #customerImageModal let-modal>
    <div class="modal-header">
      <h4 class="modal-title">
        <i class="fas fa-image"></i>
        @if (selectedImage && selectedImage.includes('user')) {
        Customer Photo
        } @else {
        Product Image
        }
      </h4>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body text-center">
      @if (selectedImage) {
      <img [src]="selectedImage" alt="Image" class="modal-image">
      } @else {
      <div class="no-image">
        <i class="fas fa-image fa-3x text-muted"></i>
        <p class="mt-3">No image available</p>
      </div>
      }
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.close()">Close</button>
    </div>
  </ng-template>
</div>